import type { ApiClient } from './ApiClient';
import { WalletError } from './errors';
import type {
  AccountMe,
  AccountPatchRequest,
  EmailStartResponse,
  EmailVerifyResponse,
  RootEnvelopeWire,
} from './types';

/**
 * Namespace class for the S0 accounts endpoints, constructed by `ApiClient`
 * exactly like `PasskeyApi`/`SessionApi`/etc. Every method goes through
 * `ApiClient.request()` — the single place that owns URL composition, the
 * `X-Request-ID` header, the `Authorization` header, and problem+json ->
 * `WalletError` translation. No fetch call or error parsing lives here.
 */
export class AccountsApi {
  constructor(private readonly client: ApiClient) {}

  /**
   * Starts email verification. No auth; the wallet-origin cookie still rides
   * along. The returned `expiresInSeconds` is the server's own OTP TTL —
   * surface it rather than restating a duration the client cannot know (see
   * `EmailStartResponse`).
   */
  async emailStart(email: string): Promise<EmailStartResponse> {
    const { data } = await this.client.request<EmailStartResponse>('POST', '/v1/accounts/email/start', {
      body: { email },
      credentials: 'include',
    });
    return data;
  }

  /**
   * Verifies the OTP code. No auth. `status` distinguishes a brand-new
   * verification from one that lands on an email already registered to
   * another account — both are successful outcomes, not errors, and still
   * issue an `emailToken`.
   */
  async emailVerify(email: string, code: string): Promise<EmailVerifyResponse> {
    const { data } = await this.client.request<EmailVerifyResponse>('POST', '/v1/accounts/email/verify', {
      body: { email, code },
      credentials: 'include',
    });
    return data;
  }

  async me(): Promise<AccountMe> {
    const { data } = await this.client.request<AccountMe>('GET', '/v1/accounts/me', {
      authRequired: true,
      credentials: 'include',
    });
    return data;
  }

  /**
   * Changes the display name, binds a verified email, or both.
   *
   * `emailToken` is the artefact `emailVerify` already mints — the mailbox is
   * proven by the SAME single-use token registration uses, so there is no
   * second OTP path to keep in step. A caller who holds one has proven
   * mailbox control, which is what makes a `409 email-conflict` from this
   * endpoint safe to show verbatim.
   *
   * A patch carrying NEITHER field is rejected here rather than sent: the
   * request would ask the server to change nothing, and the only thing a
   * round trip could add is a 400 the caller has to translate back. The type
   * already forbids it; this catches the callers tsc cannot see (untyped
   * JS, a value widened through `any`).
   */
  async patchMe(patch: AccountPatchRequest): Promise<AccountMe> {
    const { displayName, emailToken } = patch as { displayName?: string; emailToken?: string };
    if (displayName === undefined && emailToken === undefined) {
      throw new WalletError('validation', 'accounts.patchMe needs displayName, emailToken, or both');
    }
    const { data } = await this.client.request<AccountMe>('PATCH', '/v1/accounts/me', {
      body: patch,
      authRequired: true,
      credentials: 'include',
    });
    return data;
  }

  /** POST-upsert despite the name: the server has no separate create/update split for envelopes. */
  async putEnvelope(envelope: RootEnvelopeWire): Promise<void> {
    await this.client.request<void>('POST', '/v1/accounts/envelopes', {
      body: envelope,
      authRequired: true,
      credentials: 'include',
    });
  }

  async getEnvelope(credentialId: string): Promise<RootEnvelopeWire> {
    const { data } = await this.client.request<RootEnvelopeWire>(
      'GET',
      // credentialId is base64url and may contain characters ('-', '_') that
      // survive unencoded, but encode unconditionally rather than assume that.
      `/v1/accounts/envelopes/${encodeURIComponent(credentialId)}`,
      { authRequired: true, credentials: 'include' },
    );
    return data;
  }
}
